import { Popover } from "@mui/material";
import { useQuery, useMutation } from "@tanstack/react-query";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { useStoreDetail } from "../../lib/apis/hooks/store.hook";
import { useModalStore } from "../../store/useModalStore";
import { usePopover } from "../../store/usePopoverStore";
import type { matchStoreForm, StoreDensityForm, storeForm, StoreUseDateForm } from "../../lib/apis/dtos/request/store.request";
import { deletePriorAuthorization, patchStore, patchStoreDensity, patchStoreUseDate, postPriorAuthorization, unmatchStore } from "../../lib/apis/v1/store.service";
import { getQueryClient } from "../../config/queryClient.config";
import ConfirmPopover from "../../components/ui/popover/confirm.component";
import ExplainTitle from "../../components/layout/header/explain-title";
import Button from "../../components/ui/button";
import { MODAL_KEY } from "../../lib/constants/modal.constants";
import type { Address } from "react-daum-postcode";
import React from "react";
import ActiveBranchHeader from "../../components/layout/header/active-branch";
import { useAuth } from "../../store/useAuthStore";
import { ROLES } from "../../types/auth.types";
import type { StoreDetailResponse } from "../../lib/apis/dtos/response/store.response";
import SuccessSnackbar from "../../components/ui/snackbar/success.component";
import FailSnackbar from "../../components/ui/snackbar/fail.component";
import Error403 from "../../components/layout/errors/403";
import { useZoneGroup } from "../../lib/apis/hooks/category.hook";
import { convertRegionData } from "../../utils/zone.utils";

const MONTHLY_PAYMENT = [
  { value: "LOW", label: "110,000" },
  { value: "HIGH", label: "220,000" },
];

export default function BranchDetailPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const parsedId =
    typeof id === "string" ? id : Array.isArray(id) ? id[0] : "0";
  const isOwnerOrPartner =
    auth.role === ROLES.OWNER || auth.role === ROLES.PARTNER;

  const { data, isSuccess } = useQuery({
    ...useStoreDetail(parsedId),
  });
  const { result, code } = data || {};

  const { refetch } = useQuery({
    ...useZoneGroup(),
    enabled: false,
  });

  const { openModal } = useModalStore();
  const pushPopover = usePopover((state) => state.pushPopover);
  const popPopover = usePopover((state) => state.popPopover);

  const patchMutation = useMutation({
    mutationFn: ({ id, form }: { id: string; form: storeForm }) =>
      patchStore({ id, form }),
    onSuccess: () => {
      getQueryClient().invalidateQueries({ queryKey: ["store-detail"] });
    },
  });

  const patchUseDateMutation = useMutation({
    mutationFn: (form: StoreUseDateForm) => patchStoreUseDate(form),
    onSuccess: () => {
      getQueryClient().invalidateQueries({ queryKey: ["store-detail"] });
    },
  });

  const patchDensityMutation = useMutation({
    mutationFn: (form: StoreDensityForm) => patchStoreDensity(form),
    onSuccess: () => {
      getQueryClient().invalidateQueries({ queryKey: ["store-detail"] });
    },
  });

  const unmatchMutation = useMutation({
    mutationFn: (form: matchStoreForm) => unmatchStore(form),
    onSuccess: () => {
      getQueryClient().invalidateQueries({ queryKey: ["store-detail"] });
    },
  });

  const priorAuthorizationMutation = useMutation({
    mutationFn: (form: { file: File, storeId: string }) => postPriorAuthorization(form.file, form.storeId),
    onSuccess: (response) => {
      const { error, message } = response;
      if (error) {
        FailSnackbar(`사전 승낙서 등록 실패(${message})`)
      } else {
        getQueryClient().invalidateQueries({ queryKey: ["store-detail"] });
        SuccessSnackbar("사전 승낙서 등록 성공")
      }
    },
  });

  const deletePriorAuthorizationMutation = useMutation({
    mutationFn: (storeId: string) => deletePriorAuthorization(storeId),
    onSuccess: (response) => {
      const { error, message } = response;
      if (error) {
        FailSnackbar(`사전 승낙서 삭제 실패(${message})`)
      } else {
        getQueryClient().invalidateQueries({ queryKey: ["store-detail"] });
        SuccessSnackbar("사전 승낙서 삭제 성공")
      }
    },
  });

  const updateStore = (fields: Partial<storeForm>) => {
    if (!result) return;
    patchMutation.mutate({
      id: parsedId,
      form: {
        ...result,
        ...fields,
      } as unknown as storeForm,
    });
  };

  const handleEditRegionDensity = () => {
    openModal(MODAL_KEY.SelectField, {
      title: "지점 월 결제금액 변경",
      menuItems: MONTHLY_PAYMENT,
      value: result?.regionDensity as string,
      label: "월 결제금액*",
      click: (value) => {
        patchDensityMutation.mutate({ id: Number(parsedId), density: value as "HIGH" | "LOW" });
      },
    });
  };

  const handleEditUseDate = () => {
    openModal(MODAL_KEY.BranchExpiration, {
      store: result as StoreDetailResponse,
      storeId: parsedId,
      click: (form) => {
        patchUseDateMutation.mutate({ id: Number(parsedId), ...form });
      },
    });
  };

  const handleEditStoreName = () => {
    openModal(MODAL_KEY.TextField, {
      title: "지점명",
      value: result?.storeName,
      click: (change: string) => updateStore({ storeName: change }),
    });
  };

  const handleEditMainZone = async () => {
    try {
      const { data: latestData } = await refetch();
      const { result: latestZoneData } = latestData || {};
      const menuItems = convertRegionData(latestZoneData?.zoneGroups || []).map((item) => ({
        label: item.name,
        value: item.zoneId,
      }));

      openModal(MODAL_KEY.SelectField, {
        menuItems,
        title: "주 영업 지역 변경",
        label: "주 영업 지역 선택*",
        value: result?.mainZone?.zoneId || "",
        click: (value) => updateStore({ ZoneId: value as number }),
      });
    } catch (error) {
      // 에러 처리 무시
    }
  };

  const handleEditSubZones = () => {
    openModal(MODAL_KEY.AssignZone, {
      mainZoneId: result?.mainZone?.zoneId || 0,
      subZones: result?.subZones || [],
      onClick: (value: number[]) => {
        updateStore({ subzoneIds: value || null });
      },
    });
  };

  const handleEditAddress = () => {
    openModal(MODAL_KEY.Postcode, {
      onComplate: async (address: Address) => {
        try {
          const { data } = await axios.get(
            `https://dapi.kakao.com/v2/local/search/address.json?query=${address.jibunAddress}`,
            {
              headers: {
                Authorization: `KakaoAK 12c86c4b63a51e85705750f0e6de28e2`,
              },
            }
          );
          const { documents } = data as { documents: { x: string; y: string }[] };
          updateStore({
            storeAddress: address.jibunAddress,
            latitude: documents[0].y,
            longitude: documents[0].x,
          });
          navigate(-1);
        } catch (error) {
          // 에러 처리 무시
        }
      },
    });
  };

  const handleEditAddressDetail = () => {
    openModal(MODAL_KEY.TextField, {
      title: "상세 주소",
      value: result?.storeAddressDetail,
      click: (change: string) => updateStore({ storeAddressDetail: change }),
    });
  };

  const handleEditStoreLink = () => {
    openModal(MODAL_KEY.TextField, {
      title: "연결 링크",
      value: result?.storeLink,
      click: (change: string) => updateStore({ storeLink: change }),
    });
  };

  const handleEditPriorAuthorization = () => {
    openModal(MODAL_KEY.FileUpload, {
      title: "사전 승낙서 변경",
      fileDescription: "사전승낙서 이미지를 선택/ 올려놓아 해주세요.",
      fileSubject: "사전승낙서 이미지 첨부",
      fileUrl: result?.priorAuthorizationUri,
      click: (files: File[]) =>
        priorAuthorizationMutation.mutate({ file: files[0], storeId: parsedId }),
      delete: () => deletePriorAuthorizationMutation.mutate(parsedId),
    });
  };

  const handleAssignUser = () => {
    openModal(MODAL_KEY.AssignUser, { storeId: parsedId });
  };

  const delMatchPopoverClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    userId: number
  ) => {
    return pushPopover(
      Popover,
      event.currentTarget,
      {
        anchorOrigin: { vertical: "bottom", horizontal: "center" },
        transformOrigin: { vertical: "top", horizontal: "center" },
        PaperProps: {
          sx: { mt: 3 },
        },
      },
      (id) => (
        <ConfirmPopover
          id={id}
          rightProps={{
            label: "배정 해제",
            variant: "danger",
            onClick: () => {
              popPopover(id);
              unmatchMutation.mutate({ id: parsedId, users: [userId] });
            },
          }}
        >
          <p>
            담당자 배정을 해제하시겠습니까?
            <br />
            지점별 담당자는 최소 1명 배정되어
            <br />
            어야합니다.
          </p>
        </ConfirmPopover>
      )
    );
  };

  if (code === 403 || code === 30001) return <Error403 />;

  return (
    <React.Fragment>
      {result?.isActive && <ActiveBranchHeader endDate={result?.endDate} />}
      <div className="p-6">
        <ExplainTitle
          title={`${result?.storeName} 관리`}
          explanation="지점 정보를 관리하세요."
          titleSuffix={
            <div className="flex-row gap-2 pl-2">
              <Button
                variant={result?.isActive && !result?.isBanned ? "primary" : "gray"}
                label={result?.isActive && !result?.isBanned ? "활성" : "비활성"}
                className="px-2.5 h-8 rounded-[100px] text-[13px]"
              />
              <span className="text-[14px] font-normal text-[#208CFF] whitespace-nowrap">
                {result?.isOnline
                  ? "온라인"
                  : result?.isActive
                    ? ""
                    : auth.role !== ROLES.ADMIN
                      ? `*지점을 활성화 시키시려면 {https://pf.kakao.com/_xdxhxlan}로 문의주세요.`
                      : ""}
              </span>
              {auth.role === ROLES.ADMIN && (
                <>
                  <p className="text-[14px] font-normal text-black/80 whitespace-nowrap">{`({월 ${result?.regionDensity === "HIGH" ? "220,000" : "110,000"
                    }})`}</p>
                  <img
                    onClick={handleEditRegionDensity}
                    src="/static/icons/icon_pencil_blue.svg"
                    width={24}
                    height={24}
                    className="cursor-pointer"
                    alt={""}
                  />
                </>
              )}
            </div>
          }
        >
          {auth.role === ROLES.ADMIN && (
            <div className="flex-row justify-end gap-2 w-full">
              <span className="text-[14px] font-normal text-black/80 whitespace-normal">
                {result?.isBanned
                  ? `정지됨 (~${result?.banTo || "-"})`
                  : result?.isActive
                    ? `사용기한 : ${result?.startDate} ~ ${result?.endDate}`
                    : "비활성지점 사용기간 입력"}
              </span>
              <img
                src="/static/icons/icon_pencil_blue.svg"
                width={24}
                height={24}
                className="ml-2 cursor-pointer"
                alt={""}
                onClick={handleEditUseDate}
              />
              <Button className="ml-4" variant="outline-red" label="지점 삭제" />
            </div>
          )}
        </ExplainTitle>
        <table className="w-full table-fixed">
          <colgroup>
            <col className="w-40" />
            <col className="w-full" />
            <col className="w-40" />
            <col className="w-full" />
          </colgroup>
          <tbody>
            <tr className="content-table-tr">
              <td className="content-table-th">지점명</td>
              <td className="content-table-td flex-row w-full" colSpan={3}>
                {result?.storeName}
                {!isOwnerOrPartner && (
                  <img
                    src="/static/icons/icon_pencil_blue.svg"
                    width={24}
                    height={24}
                    onClick={handleEditStoreName}
                    className="ml-2 cursor-pointer"
                    alt={""}
                  />
                )}
              </td>
            </tr>
            {!result?.isOnline && (
              <tr className="content-table-tr">
                <td className="content-table-th">주 영업 지역</td>
                <td className="content-table-td flex-row">
                  {result?.mainZone?.region} {result?.mainZone?.zoneName}
                  {!isOwnerOrPartner && (
                    <img
                      src="/static/icons/icon_pencil_blue.svg"
                      width={24}
                      height={24}
                      onClick={handleEditMainZone}
                      className="ml-2 cursor-pointer"
                      alt={""}
                    />
                  )}
                </td>
                <td className="content-table-th">인근 영업 지역</td>
                <td className="content-table-td flex-row">
                  {result?.subZones.map((item) => `${item.region} ${item.zoneName}`).join(", ") || "-"}
                  {!isOwnerOrPartner && (
                    <img
                      src="/static/icons/icon_pencil_blue.svg"
                      width={24}
                      height={24}
                      onClick={handleEditSubZones}
                      className="ml-2 cursor-pointer"
                      alt={""}
                    />
                  )}
                </td>
              </tr>
            )}
            <tr className="content-table-tr">
              <td className="content-table-th">지점 주소</td>
              <td className="content-table-td flex-row">
                {result?.storeAddress}
                {!isOwnerOrPartner && (
                  <img
                    src="/static/icons/icon_pencil_blue.svg"
                    width={24}
                    height={24}
                    onClick={handleEditAddress}
                    className="ml-2 cursor-pointer"
                    alt={""}
                  />
                )}
              </td>
              <td className="content-table-th">상세 주소</td>
              <td className="content-table-td flex-row">
                {result?.storeAddressDetail}
                {!isOwnerOrPartner && (
                  <img
                    src="/static/icons/icon_pencil_blue.svg"
                    width={24}
                    height={24}
                    onClick={handleEditAddressDetail}
                    className="ml-2 cursor-pointer"
                    alt={""}
                  />
                )}
              </td>
            </tr>
            <tr className="content-table-tr">
              <td className="content-table-th">연결 링크</td>
              <td className="content-table-td flex-row">
                {result?.storeLink || "-"}
                <img
                  src="/static/icons/icon_pencil_blue.svg"
                  width={24}
                  height={24}
                  onClick={handleEditStoreLink}
                  className="ml-2 cursor-pointer"
                  alt={""}
                />
              </td>
            </tr>
            <tr className="content-table-tr">
              <td className="content-table-th">사전 승낙서 파일</td>
              <td className="content-table-td">
                <div className="flex-row gap-2">
                  {result?.priorAuthorizationUri && (
                    <span
                      className="cursor-pointer text-primary-50 font-normal"
                      onClick={() =>
                        openModal(MODAL_KEY.Prior, {
                          priorAuthorizationUri: result?.priorAuthorizationUri || "",
                        })
                      }
                    >
                      {result?.priorAuthorizationFileName || ""}
                    </span>
                  )}
                  <img
                    onClick={handleEditPriorAuthorization}
                    src="/static/icons/icon_pencil_blue.svg"
                    width={24}
                    height={24}
                    className="ml-2 cursor-pointer"
                    alt={""}
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        {/* 담당자 정보 */}
        {isSuccess &&
          (result?.users || []).map(
            (
              {
                username,
                phone,
                userId,
              }: {
                username: string;
                phone: string;
                userId: number;
              },
              index: number
            ) => {
              return (
                <div
                  key={index}
                  className="border border-[rgba(255,255,255, 0.12)] p-[24px] w-full mt-[24px] rounded-[8px]"
                >
                  <div className="flex flex-row justify-between items-center">
                    <h2 className="p-[16px] pt-0 text-[22px] ">담당자 정보</h2>
                    <Button variant="outline-red" label="배정 해제" onClick={(e) => delMatchPopoverClick(e, userId)} />
                  </div>
                  <table className="w-full">
                    <tbody>
                      <tr className="content-table-tr">
                        <td className="content-table-th">이름</td>
                        <td className="content-table-td">{username || "-"}</td>
                        <td className="content-table-th">연락처</td>
                        <td className="content-table-td">{phone || "-"}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              );
            }
          )}

        <Button className="mt-[24px]" variant={"outline"} label="담당자 배정" onClick={handleAssignUser} />
      </div>
    </React.Fragment>
  );
}
